import { Controller } from "@hotwired/stimulus"
import TableFrameNavigator from "../../utils/table_frame_navigator"
import TableQueryState from "../../utils/table_query_state"

// Connects to data-controller="bank--transaction-filter"
// Handles filter logic and URL construction for bank transactions
export default class extends Controller {
  static targets = [
    "bankAccountId",
    "dateFrom",
    "dateTo",
    "amountFrom",
    "amountTo",
    "matchingStatusCheckbox",
    "classificationDetailCheckbox",
    "loanIdCheckbox",
    "order",
    "search"
  ]
  static outlets = ["bank--transaction-filter-menu"]

  static singleValueTargets = ["bankAccountId", "dateFrom", "dateTo", "amountFrom", "amountTo", "order", "search"]

  // Maps target names to snake_case URL param names
  #paramNames = {
    bankAccountId: "bank_account_id",
    dateFrom: "date_from",
    dateTo: "date_to",
    amountFrom: "amount_from",
    amountTo: "amount_to",
    order: "order",
    search: "search"
  }

  connect() {
    this.frameId = this.element.closest("turbo-frame#transactions_list")?.id || "transactions_list"
    this.tableNavigator = new TableFrameNavigator({
      frameId: this.frameId,
      tableKey: "bank:transactions",
      onPopState: (queryState) => this.setFilterValues(queryState)
    })
    this.boundNavigationRollback = this.#handleNavigationRollback.bind(this)
    document.addEventListener("lucanto:table-navigation-rollback", this.boundNavigationRollback)
    this.setFilterValues()
  }

  disconnect() {
    document.removeEventListener("lucanto:table-navigation-rollback", this.boundNavigationRollback)
    this.tableNavigator?.disconnect()
  }

  setFilterValues(queryState = new TableQueryState(window.location.search)) {
    const urlSearchParams = queryState.toSearchParams()

    // Set single-value targets (reset to empty when param is absent)
    this.constructor.singleValueTargets.forEach((targetName) => {
      const hasTargetProperty = `has${this.#capitalizeFirstLetter(targetName)}Target`
      if (this[hasTargetProperty]) {
        const target = this[`${targetName}Target`]
        const paramName = this.#paramNames[targetName] || targetName
        if (target) {
          target.value = urlSearchParams.get(paramName) || ""
        }
      }
    })

    // Set matching status checkboxes
    const matchingStatusValues = urlSearchParams.getAll("matching_status[]")
    this.matchingStatusCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = matchingStatusValues.includes(checkbox.value)
    })

    // Set classification detail checkboxes
    const classificationDetailValues = urlSearchParams.getAll("classification_detail[]")
    this.classificationDetailCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = classificationDetailValues.includes(checkbox.value)
    })

    // Set loan checkboxes
    const loanIdValues = urlSearchParams.getAll("loan_id[]")
    this.loanIdCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = loanIdValues.includes(checkbox.value)
    })
  }

  activeStatusChecker(event) {
    if (!this.hasBankTransactionFilterMenuOutlet) {
      return
    }
    const filterMenuController = this.bankTransactionFilterMenuOutlet
    const mapping = {
      bank_account_id: [["bankAccountIdTarget"], filterMenuController.activeStatusBankAccountTarget],
      date: [["dateFromTarget", "dateToTarget"], filterMenuController.activeStatusDateTarget],
      amount: [["amountFromTarget", "amountToTarget"], filterMenuController.activeStatusAmountTarget],
      classification_detail: [
        null,
        filterMenuController.hasActiveStatusDetailTarget ? filterMenuController.activeStatusDetailTarget : null
      ],
      matching_status: [null, filterMenuController.activeStatusStatusTarget],
      loan_id: [null, filterMenuController.activeStatusLoanTarget],
      order: [["orderTarget"], filterMenuController.activeStatusOrderTarget]
    }

    for (const key in mapping) {
      if (event.target.name.includes(key)) {
        const [targets, statusTarget] = mapping[key]
        if (statusTarget) {
          if (key === "matching_status") {
            const hasChecked = this.matchingStatusCheckboxTargets.some((cb) => cb.checked)
            filterMenuController.toggleActiveStatus(hasChecked, statusTarget)
          } else if (key === "classification_detail") {
            const hasChecked = this.classificationDetailCheckboxTargets.some((cb) => cb.checked)
            filterMenuController.toggleActiveStatus(hasChecked, statusTarget)
          } else if (key === "loan_id") {
            const hasChecked = this.loanIdCheckboxTargets.some((cb) => cb.checked)
            filterMenuController.toggleActiveStatus(hasChecked, statusTarget)
          } else {
            this.#statusChecker(filterMenuController, targets, statusTarget)
          }
        }
        break
      }
    }
  }

  cancelFilters() {
    this.removeValuesFromFilters()
    this.#constructUrlAndSendRequest()
  }

  removeValuesFromFilters() {
    this.constructor.singleValueTargets.forEach((targetName) => {
      const hasTargetProperty = `has${this.#capitalizeFirstLetter(targetName)}Target`
      if (this[hasTargetProperty]) {
        const target = this[`${targetName}Target`]
        target.value = ""
        target.disabled = false
      }
    })
    this.matchingStatusCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = false
    })
    this.classificationDetailCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = false
    })
    this.loanIdCheckboxTargets.forEach((checkbox) => {
      checkbox.checked = false
    })
  }

  cancelChipFilter(event) {
    const targetName = event.currentTarget.dataset.target
    if (targetName === "matchingStatus") {
      this.matchingStatusCheckboxTargets.forEach((cb) => {
        cb.checked = false
      })
      this.#constructUrlAndSendRequest()
      return
    }
    if (targetName === "classificationDetail") {
      this.classificationDetailCheckboxTargets.forEach((cb) => {
        cb.checked = false
      })
      this.#constructUrlAndSendRequest()
      return
    }
    if (targetName === "loanId") {
      this.loanIdCheckboxTargets.forEach((cb) => {
        cb.checked = false
      })
      this.#constructUrlAndSendRequest()
      return
    }
    const target = this[`${targetName}Target`]
    if (target) {
      target.value = ""
      target.disabled = false
      this.#constructUrlAndSendRequest()
    }
  }

  applyFilters(event) {
    this.#constructUrlAndSendRequest({ search: this.hasSearchTarget && event?.target === this.searchTarget })
  }

  #constructUrlAndSendRequest({ search = false } = {}) {
    const queryState = this.#constructQueryState()
    if (search) {
      // Deliberately NO restoreFocusSelector, unlike contacts: this list's search
      // field is `data-turbo-permanent`, so Turbo MOVES the same node into the
      // replacement frame (FrameRenderer#render → preservingPermanentElements)
      // and its Bardo re-focuses whatever held focus inside it. Naming a restore
      // target on top of that re-runs table_frame_navigator's
      // setSelectionRange(end, end) up to three times per render, which yanks the
      // caret to the end of the field mid-word. Contacts needs the option because
      // its field is NOT permanent and is genuinely rebuilt.
      this.tableNavigator.scheduleSearch(queryState, { preserveScroll: { x: true, y: false } })
    } else {
      this.tableNavigator.navigate(queryState, {
        history: "push",
        reason: "filter",
        preserveScroll: { x: true, y: false }
      })
    }
  }

  #statusChecker(filterMenuController, targets, statusTarget) {
    const status = targets.some((target) => {
      const targetName = target.replace("Target", "")
      const hasTargetProperty = `has${this.#capitalizeFirstLetter(targetName)}Target`
      if (!this[hasTargetProperty]) {
        return false
      }
      const element = this[target]
      if (element.type === "checkbox") {
        return element?.checked
      } else {
        return element.value
      }
    })
    filterMenuController.toggleActiveStatus(status, statusTarget)
  }

  #constructQueryState() {
    const changes = Object.fromEntries(Object.values(this.#paramNames).map((paramName) => [paramName, null]))
    changes["matching_status[]"] = []
    changes["classification_detail[]"] = []
    changes["loan_id[]"] = []
    this.#setSingleValueChanges(changes)
    this.matchingStatusCheckboxTargets.forEach((checkbox) => {
      if (checkbox.checked && checkbox.value !== "") {
        changes["matching_status[]"].push(checkbox.value)
      }
    })
    this.classificationDetailCheckboxTargets.forEach((checkbox) => {
      if (checkbox.checked && checkbox.value !== "") {
        changes["classification_detail[]"].push(checkbox.value)
      }
    })
    this.loanIdCheckboxTargets.forEach((checkbox) => {
      if (checkbox.checked && checkbox.value !== "") {
        changes["loan_id[]"].push(checkbox.value)
      }
    })
    let currentState = new TableQueryState(window.location.search)
    const direction = currentState.toSearchParams().get("direction")
    if (direction && !["asc", "desc"].includes(direction)) {
      currentState = currentState.patch({ direction: null }, { resetPage: false })
    }
    return currentState.patch(changes)
  }

  #setSingleValueChanges(changes) {
    this.constructor.singleValueTargets.forEach((targetName) => {
      const hasTargetProperty = `has${this.#capitalizeFirstLetter(targetName)}Target`
      if (this[hasTargetProperty]) {
        const target = this[`${targetName}Target`]
        const paramName = this.#paramNames[targetName] || targetName
        if (target?.value && target?.value !== "") {
          changes[paramName] = target.value
        }
      }
    })
  }

  #capitalizeFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  #handleNavigationRollback(event) {
    if (event.detail?.frameId !== this.frameId || event.detail?.tableKey !== "bank:transactions") return
    this.setFilterValues(new TableQueryState(window.location.search))
    if (this.hasBankTransactionFilterMenuOutlet) {
      this.bankTransactionFilterMenuOutlet.refreshActiveFilterIndicator()
    }
  }
}
